import { Suspense, lazy, useMemo, useEffect, useState } from 'react';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Tabs } from '@uhg-abyss/web/ui/Tabs';
import { Button } from '@uhg-abyss/web/ui/Button';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import {
  CustomerProfileProvider,
  useCustomerProfileForm,
} from './useCustomerProfileForm';
import {
  useLazyGetProfileBySubmitterQuery,
  useLazyGetProfileByVersionQuery,
  useLazyGetProfileListQuery,
  useLazyGetProfileQuery,
  useLazyGetProfileSearchQuery,
  useLazySetProfileQuery,
  useLazyUpdateProfileQuery,
} from '@src/store/services/profileServices';
import SearchBar from './SearchBar';
import { useAuth } from '@src/Providers/AuthProvider';
import {
  convertStrintoArray,
  convertToCST,
  footerDateformat,
  formtDate,
  getIdsByName,
  getThelistData,
  getVersionListBySubmitterGroupName,
  modifyArray,
  validateInput,
  validateInputs,
  validateNextExpedAndEarlyStart,
  validatePolicyNumber,
  validateProfile,
  validateStreetorCity,
  validateVersionEndDate,
  decryptProfileParams,
  encryptProfileParams,
} from '@src/utils';
import { props } from './Profile.types';
import { Search } from '../HelpPages/Search';
import { LABELS, dSystems } from '@src/constants';
import { Box } from '@uhg-abyss/web/ui/Box';
import { useDispatch, useSelector } from 'react-redux';
import {
  SetVerEndDate,
  setEarlyExpdDate,
  setLovData,
  setNextFileIndicator,
  setSubmitterdata,
  setVerStartDate,
  setVesrionData,
} from '@src/store/slices/ListofValuesSlice';
import { setErrors } from '@src/store/slices/errormessageSlice';
import { RootState } from '@src/store/AppStore';
import { useToast } from '@uhg-abyss/web/hooks/useToast';

const ProfileDetails = lazy(() => import('./ProfileDetails'));
const FileDetails = lazy(() => import('./FileDetails'));
const Demographics = lazy(() => import('./Demographics'));
const ThresholdProcessing = lazy(() => import('./ThresholdProcessing'));
const RulesMaintenance = lazy(() => import('./RulesMaintenance'));
const PopulationExclusions = lazy(() => import('./PopulationExclusions'));
const ContactInformation = lazy(() => import('./ContactInformation'));
const Mappings = lazy(() => import('./Mappings'));

const CustomerProfileCard: React.FC<props> = ({ isSearch }) => {
  const {
    editMode,
    handleReset,
    toggleEditMode,
    isSearchEnabled,
    form,
    data,
    profileLists,
    loadStatus,
    setFieldErrorMessages,
    fieldErrorMessages,
    handleEditButton,
    handleSubmit,
  } = useCustomerProfileForm();

  const dispatch = useDispatch();
  const { user } = useAuth();
  const router = useRouter();
  const { encryptedData: profileEncryptedData } = router.getRouteParams();
  const { version, asgnId, srchDestSysCd } = profileEncryptedData
    ? decryptProfileParams(profileEncryptedData)
    : { version: undefined, asgnId: undefined, srchDestSysCd: undefined };
  const errors = useSelector(
    (state: RootState) => state?.errorMessages?.errors as string[]
  );
  const errrstoshow = { ...fieldErrorMessages, ...errors };
  const errorMessag: string[] = Object.values(errrstoshow).filter(
    (error): error is string => error !== null && error !== undefined
  );

  const dsystem = form.watch('customerDetails.destinationSystem');
  const profileStatus = form.watch('customerDetails.profileStatus');
  const useDetails = form.watch('profileDetails');
  const systemType = getThelistData(profileLists.destinationSystems, dsystem);

  const isGPSorPRIME =
    systemType === dSystems.GPS || systemType === dSystems.PRIME;
  const isGPS = systemType === dSystems.GPS;
  const isPrime = systemType == dsystem;
  const [activeIndex, setActiveIndex] = useState(0);
  const handleEdit = () => {
    handleEditButton();
  };
  const handleMove = () => {
    router.navigate(`/profile/move`);
  };

  return (
    <>
      <div
        style={{
          padding: '16px 24px',
        }}
      >
        <Box color="$web.semantic.color.surface.container.secondary">
          {errorMessag?.length >= 1 && (
            <div>
              <Text asElement="label" css={{ color: 'red' }}>
                Validation Errors:
              </Text>
              <ul>
                {errorMessag.map((item, index) => (
                  <Text
                    css={{
                      color: ' #FF0000',
                      listStyle: 'initial',
                      fontWeight: 'bold',
                    }}
                  >
                    <li key={index}>{item}</li>
                  </Text>
                ))}
              </ul>
            </div>
          )}
        </Box>

        <Heading level={5} size="sm">
          Customer Profile
        </Heading>
        <SearchBar isSearch={isSearch} />
      </div>

      {(isSearchEnabled || version || asgnId || srchDestSysCd) && (
        <Card
          css={{
            border: 0,
            minHeight: '80vh',
            '.abyss-table-cell': {
              paddingTop: '2px',
              paddingBottom: '2px',
              width: '50%',
            },
            '.abyss-select-input-container,.abyss-text-input': {
              minHeight: '26px',
              maxWidth: '100%',
              lineHeight: '0.9',
              borderRadius: '0',
            },
            '.abyss-select-input-root .abyss-label-container,.abyss-text-input-root .abyss-label-container':
              {
                display: 'none',
              },
            '.abyss-select-input-container-wrapper': {
              height: '26px',
              borderRadius: '0',
            },
          }}
        >
          {loadStatus ? (
            <Box
              align="center"
              color="$web.semantic.color.surface.container.secondary"
            >
              <LoadingSpinner
                size="lg"
                color="$web.semantic.color.icon.interactive.rest.tertiary"
                variant="alt"
                isLoading={loadStatus}
                ariaLoadingLabel="Loading"
              />
              <h6>Loading Please Wait</h6>
            </Box>
          ) : (
            data && (
              <>
                <div>
                  <Grid>
                    <Suspense fallback="loading..">
                      <ProfileDetails />
                    </Suspense>
                    <Grid.Col
                      css={{
                        width: '100%',
                      }}
                    >
                      <Tabs
                        title="Display - Row"
                        active={activeIndex}
                        onTabChange={setActiveIndex}
                        css={{
                          '[role="tablist"]': {
                            width: '100%',
                          },
                          '.abyss-tabs-tab': {
                            flex: 1,
                          },
                          '.abyss-tabs-tab-content': {
                            fontSize: '$web.core.font-size.p.60',
                            color: '$web.semantic.color.text.content.primary',
                          },
                          '.abyss-table-cell:first-child': {
                            color: '$web.semantic.color.text.content.primary',
                            fontWeight: 'bold',
                          },
                        }}
                      >
                        <Tabs.Tab label="File Details">
                          <Suspense fallback="loading..">
                            <FileDetails />
                          </Suspense>
                        </Tabs.Tab>
                        {isGPSorPRIME ? (
                          ''
                        ) : (
                          <Tabs.Tab label="Demographics">
                            <Suspense fallback="loading..">
                              <Demographics />
                            </Suspense>
                          </Tabs.Tab>
                        )}
                        <Tabs.Tab label="Threshold Processing">
                          <Suspense fallback="loading..">
                            <ThresholdProcessing />
                          </Suspense>
                        </Tabs.Tab>
                        <Tabs.Tab label="Rules Maintenance">
                          <Suspense fallback="loading..">
                            <RulesMaintenance />
                          </Suspense>
                        </Tabs.Tab>
                        {isGPSorPRIME ? (
                          ''
                        ) : (
                          <Tabs.Tab label="Population Exclusions">
                            <Suspense fallback="loading..">
                              <PopulationExclusions />
                            </Suspense>
                          </Tabs.Tab>
                        )}
                        <Tabs.Tab label="Contact Information">
                          <Suspense fallback="loading..">
                            <ContactInformation />
                          </Suspense>
                        </Tabs.Tab>
                        {/* {editMode ? (
                          <Tabs.Tab label="Mappings">
                            <Suspense fallback="loading..">
                              <Mappings />
                            </Suspense>
                          </Tabs.Tab>
                        ) : null} */}
                      </Tabs>
                    </Grid.Col>
                    <Grid.Col
                      css={{
                        display: 'flex',
                        width: '100%',
                        gap: '1rem',
                        justifyContent: 'flex-end',
                        '.abyss-button-content-container': {
                          fontSize: '$web.core.font-size.p.60',
                        },
                      }}
                    >
                      {editMode ? (
                        <>
                          {(activeIndex == 5 && editMode) ||
                          ((dsystem === 'G' || dsystem === '5') &&
                            activeIndex == 3 &&
                            editMode) ? null : (
                            <>
                              <Button
                                onClick={() => {
                                  handleReset();
                                  toggleEditMode();
                                  dispatch(setErrors([]));
                                  setFieldErrorMessages([]);
                                }}
                                size="sm"
                                variant="outline"
                                className="no-margin-left no-padding-left no-padding-right"
                              >
                                <strong>Cancel</strong>
                              </Button>
                              <Button
                                size="sm"
                                onClick={handleSubmit}
                                variant="outline"
                                className="no-margin-left no-padding-left no-padding-right"
                              >
                                <strong>Save</strong>
                              </Button>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          {user?.editProfile ? (
                            <Button
                              onClick={() => {
                                toggleEditMode();
                                handleEdit();
                              }}
                              size="sm"
                              variant="outline"
                              className="no-margin-left no-padding-left no-padding-right"
                            >
                              <strong>Edit</strong>
                            </Button>
                          ) : null}
                          {user?.copyProfile ? (
                            <Button
                              size="sm"
                              onClick={() => {
                                router.navigate('/profile/copy');
                              }}
                              variant="outline"
                              className="no-margin-left no-padding-left no-padding-right"
                            >
                              <strong>Copy</strong>
                            </Button>
                          ) : null}
                          {user?.moveProfile
                            ? profileStatus == 1 && (
                                <Button
                                  size="sm"
                                  onClick={handleMove}
                                  variant="outline"
                                  className="no-margin-left no-padding-left no-padding-right"
                                >
                                  <strong>Move</strong>
                                </Button>
                              )
                            : null}
                        </>
                      )}
                    </Grid.Col>
                    <Grid.Col css={{ width: '100%' }}>
                      <Box
                        color="$web.semantic.color.surface.container.primary"
                        padding={0}
                        width="100%"
                        height="20px"
                        align="center"
                      >
                        <Text
                          asElement="label"
                          color="$web.semantic.color.text.content.primary-alt"
                          css={{ fontSize: '14px', fontWeight: 'bold' }}
                        >
                          UPDATED ON{' '}
                          {`${footerDateformat(useDetails?.lastUpdatedDate)}`}{' '}
                          BY{' '}
                          {`${
                            useDetails?.eeUserId ? useDetails?.eeUserId : '-'
                          }`}
                        </Text>
                      </Box>
                      <Divider orientation="horizontal" />
                    </Grid.Col>
                  </Grid>
                </div>
              </>
            )
          )}
        </Card>
      )}
    </>
  );
};

const CustomerProfile: React.FC<props> = ({ isSearch = false }) => {
  const [data, setData] = useState<Record<any, any> | null>({});
  const [search, setSearch] = useState('');
  const [demIndex, setDemIndex] = useState(0);
  const [demList, setDemList] = useState([]);
  const [errMsg, setErrMsg] = useState('');
  const [profileLists, setProfileLists] = useState([]);
  const [submitterList, setSubmitterList] = useState([]);
  const [submitterValue, setSubmitterValue] = useState();
  const [loadStatus, setLoadStatus] = useState(false);
  const [vNumber, setVNumber] = useState('');
  const [latestVersion, setLatestVersion] = useState();
  const [cancelClick, setCancelClick] = useState<boolean>();
  const router = useRouter();
  const [vlist, setVlist] = useState([]);
  const { encryptedData: profileEncryptedData } = router.getRouteParams();
  const { version, asgnId, srchDestSysCd } = profileEncryptedData
    ? decryptProfileParams(profileEncryptedData)
    : { version: undefined, asgnId: undefined, srchDestSysCd: undefined };
  const [profileTrigger] = useLazyGetProfileQuery();

  const [profileAllFields] = useLazyGetProfileListQuery();
  const [profileBysybmitter] = useLazyGetProfileBySubmitterQuery();
  const [profileSearchTrigger] = useLazyGetProfileSearchQuery();

  const [profileByversion] = useLazyGetProfileByVersionQuery();
  const [updateProfile] = useLazyUpdateProfileQuery();
  const [fieldErrorMessages, setFieldErrorMessages] = useState<string[]>([]);
  const [editMode, setEditMode] = useState(false);

  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { toast } = useToast();
  const profileStateData = useSelector((state) => state.listOfValues);
  const {
    versionEffectiveDate,
    versionEndDate,
    nextExpCtDate,

    earlyDate,
  } = profileStateData;
  const toggleEditMode = () => setEditMode((prev) => !prev);

  const [submitterDetails, setSubmitterDetails] = useState({
    submitterAssignId: '',
    submitterGroupId: '',
    submitterGroupName: '',
    versionNumber: '',
    destinationSystem: '',
  });
  const updateSubmitterDeatils = (updates) => {
    setSubmitterDetails((prevObject) => ({ ...prevObject, ...updates }));
  };

  const form = useForm({
    defaultValues: {
      ...data,
    },
  });

  useEffect(() => {
    if (data && Object.keys(data).length) form.reset(data);
  }, [data]);
  useEffect(() => {
    const handleFetchFieldsData = async () => {
      const { data } = await profileAllFields({});
      setProfileLists(data);
      dispatch(setLovData(data));
    };
    handleFetchFieldsData();
  }, []);

  useEffect(() => {
    if (isSearch) return;
    if (version && asgnId && srchDestSysCd) {
      (async () => {
        setLoadStatus(true);
        const { data } = await profileTrigger({
          version,
          asgnId,
          srchDestSysCd,
        });
        setLoadStatus(false);
        setData(data);
        dispatch(setVesrionData(data.history));
        setSubmitterList(data?.history);
        dispatch(setSubmitterdata(data?.customerDetails));
        setSubmitterValue(data?.customerDetails?.submitterGroupName);
        const versionList = getVersionListBySubmitterGroupName(
          data?.history,
          data?.customerDetails?.submitterGroupName
        );
        setVlist(versionList);
        updateSubmitterDeatils({
          submitterAssignId: asgnId,
          submitterGroupId: data?.customerDetails?.submitterGroupId,
          submitterGroupName: data?.customerDetails.submitterGroupName,
          destinationSystem: data?.customerDetails?.destinationSystem,
        });

        setVNumber(version);
        dispatch(SetVerEndDate(data?.customerDetails?.versionEndDate));
        dispatch(setEarlyExpdDate(data?.demographics?.earliestStartDate));
        dispatch(setVerStartDate(data?.customerDetails?.versionEffectiveDate));
        dispatch(setNextFileIndicator(data?.fileDetails?.nextFileExpectedDate));
      })();
    }
  }, []);

  const fetchDataBysearch = async (search) => {
    setLoadStatus(true);
    const response = await profileSearchTrigger({
      search,
    });
    setLoadStatus(false);
    const { data, isSuccess, isLoading } = response;
    if (!isSuccess) {
      setData(null);
      form.reset({});
      setSubmitterList([]);
      setVlist([]);
      setErrMsg(response?.error?.response?.data?.message);
    } else {
      setData(data);
      dispatch(setVesrionData(data?.history));
      setLoadStatus(isLoading);
      setSubmitterList(data?.history);
      setLatestVersion(data?.customerDetails?.versionNumber);
      setSubmitterValue(data?.customerDetails?.submitterGroupName);

      const versionList = getVersionListBySubmitterGroupName(
        data?.history,
        data?.customerDetails?.submitterGroupName
      );

      setVlist(versionList);
      setVNumber(data?.customerDetails?.versionNumber);
      updateSubmitterDeatils({
        submitterAssignId: data?.customerDetails?.submitterAssignId,
        submitterGroupId: data?.customerDetails?.submitterGroupId,
        submitterGroupName: data?.customerDetails?.submitterGroupName,
        destinationSystem: data?.customerDetails?.destinationSystem,
      });
      dispatch(setSubmitterdata(data?.customerDetails));
      dispatch(SetVerEndDate(data?.customerDetails?.versionEndDate));
      dispatch(setVerStartDate(data?.customerDetails?.versionEffectiveDate));
      dispatch(setNextFileIndicator(data?.fileDetails?.nextFileExpectedDate));
      dispatch(setEarlyExpdDate(data?.demographics?.earliestStartDate));
    }
  };

  const fetchProfileBySumbitterChange = async (details) => {
    const { data } = await profileBysybmitter({
      ...details,
    });
    setData(data);

    dispatch(setSubmitterdata(data?.customerDetails));
  };

  const onVersionChange = async (number) => {
    const { data } = await profileByversion({
      ...submitterDetails,
      versionNumber: number,
    });
    setData(data);
    setVNumber(number);
    dispatch(setSubmitterdata(data?.customerDetails));
  };

  const handleEditButton = async () => {
    const search = submitterDetails?.submitterAssignId;
    const response = await profileSearchTrigger({
      search,
    });
    const { data, isSuccess, isLoading } = response;
    setData(data);
    setVNumber(data?.customerDetails?.versionNumber);
  };

  const handleSubmit = async (x) => {
    const destination = submitterDetails?.destinationSystem;
    let errors = {
      customerIdError: validateInputs(
        LABELS.ASSIGN_SUBMISSION_ID,
        form.watch('customerDetails.submitterAssignId')
      ),
      customerSubmissionName: validateInputs(
        LABELS.CUSTOMER_SUBMISSION_NAME,
        form.watch('customerDetails.submitterGroupName')
      ),
      ...(destination === '8' && {
        customerNumber: validateInputs(
          LABELS.CUSTOMER_NUMBER,
          form.watch('customerDetails.customerNumber')
        ),
        polocyNumberError: validatePolicyNumber(
          LABELS.POLOCY_NUMBER,
          form.watch('customerDetails.policyNumber')
        ),
      }),
      profileStatus: validateProfile(
        LABELS.PROFILE_STATUS,
        form.watch('customerDetails.profileStatus')
      ),
      profileType: validateProfile(
        LABELS.PROFILE_TYPE,
        form.watch('customerDetails.profileType')
      ),
      destinationSystemError: validateStreetorCity(
        'Destination System',
        form.watch('customerDetails.destinationSystem')
      ),
      versionEffectiveDate: validateInput(
        LABELS.VERSION_EFFECTIVE_DATE,
        versionEffectiveDate
      ),
      earlyStartDateError: validateNextExpedAndEarlyStart(
        'Earliest Start Date',
        earlyDate
      ),
      nextFilexpectDateError: validateNextExpedAndEarlyStart(
        'Next File Expected Date',
        nextExpCtDate
      ),
      versionENdDate: validateNextExpedAndEarlyStart(
        LABELS.VERSION_END_DATE,
        versionEndDate
      ),

      ...(form.watch('customerDetails.versionEndDate') && {
        VersionEndError: validateVersionEndDate(
          'Version End Date',
          form.watch('customerDetails.versionEffectiveDate'),
          form.watch('customerDetails.versionEndDate')
        ),
      }),
      fileFrequencyError: validateInputs(
        LABELS.FILE_FREQUENCY,
        form.watch('fileDetails.frequencyCode')
      ),
      fileFormatError: validateInputs(
        LABELS.FILE_FORMAT_CODE,
        form.watch('fileDetails.fileFormatTypeCode')
      ),
      eligilbitysoucseError: validateInputs(
        LABELS.ELIGYBILTY_CODE,
        form.watch('fileDetails.eligibilitySourceType')
      ),
      channelTypeError: validateInputs(
        LABELS.CHANNEL_TYPE,
        form.watch('fileDetails.transmitCode')
      ),
    };

    const data = form.getValues();

    const gpsForm = form.watch('customerDetails.employerGroups');

    const formattedData = data?.thresholdProcessing?.percentages.map(
      ({ index, ...rest }) => rest
    );
    const modifiedArray = modifyArray(formattedData);

    const thresholdPrecentage =
      submitterDetails.destinationSystem === '8' ||
      submitterDetails.destinationSystem === 'G'
        ? formattedData
        : modifiedArray;

    const ids = getIdsByName(
      form.getValues('rulesMaintenance'),
      profileLists?.rulesList
    );

    const formatted = gpsForm?.map((item) => ({
      employerGroupId: item.employerGroupId,
      displayBlockNumber: item.displayBlockNumber.toString(),
    }));
    const versionEffdate = convertToCST(versionEffectiveDate);

    const versionEnddat = convertToCST(versionEndDate);

    const nextFileExpectedDate = convertToCST(nextExpCtDate);

    const earlyExpDate = convertToCST(
      form.getValues('demographics.earliestStartDate')
    );

    const formFileDeatils = form?.getValues('fileDetails');

    const typeCodeField =
      data?.populationExclusions?.TypeCodeField === ''
        ? []
        : data?.populationExclusions?.TypeCodeField;

    const CancelReasonFied =
      data?.populationExclusions?.CancelReasonField === ''
        ? []
        : data?.populationExclusions?.CancelReasonField;
    const populationEligibilityStatusField =
      data?.populationExclusions?.EligibilityStatusField === ''
        ? []
        : data?.populationExclusions?.EligibilityStatusField;

    const typePopulation =
      Array.isArray(data?.populationExclusions?.Type) &&
      data?.populationExclusions?.Type?.some(
        (type) => type !== '' && type !== null && type !== undefined
      )
        ? data?.populationExclusions?.Type.filter(
            (type) => type !== '' && type !== null && type !== undefined
          )
        : [];

    const finalPayload = {
      customerDetails: {
        submitterAssignId: data?.customerDetails?.submitterAssignId,
        submitterGroupId: data?.customerDetails?.submitterGroupId,
        submitterGroupName: data?.customerDetails?.submitterGroupName,
        ...((submitterDetails.destinationSystem === '8' ||
          submitterDetails?.destinationSystem === '1') && {
          customerNumber: data?.customerDetails?.customerNumber,
          policyNumber: data?.customerDetails?.policyNumber,
          billingEntity: data?.customerDetails?.billingEntity,
        }),

        destinationSystem: data?.customerDetails?.destinationSystem,
        legalEntity: data?.customerDetails?.legalEntity,
        profileType: data?.customerDetails?.profileType,
        profileStatus: data?.customerDetails?.profileStatus,
        versionNumber: data?.customerDetails?.versionNumber,
        marketSegment: data?.customerDetails.marketSegment,
        securityAccess: data?.customerDetails.securityAccess,
        complexityRating: +data?.customerDetails?.complexityRating,

        versionEffectiveDate: versionEffdate,
        versionEndDate: versionEnddat,
        ...(submitterDetails?.destinationSystem === 'G' && {
          employerGroups: formatted.filter((entry) => entry?.employerGroupId),
        }),
      },
      profileDetails: {
        eeUserId: user?.userId,
      },
      fileDetails: {
        frequencyCode: formFileDeatils?.frequencyCode,
        fileIndicator: formFileDeatils?.fileIndicator,
        fileFormatTypeCode: formFileDeatils?.fileFormatTypeCode,
        transmitCode: formFileDeatils?.transmitCode,
        nextFileExpectedDate: nextFileExpectedDate,
        eligibilitySourceType: formFileDeatils?.eligibilitySourceType,
        vbdMappingIndicator: formFileDeatils?.vbdMappingIndicator,
        feedVendor: formFileDeatils?.feedVendor || '0',
        ...(submitterDetails?.destinationSystem === 'G' && {
          billGroupIndicator: formFileDeatils?.billGroupIndicator,
          series800Indicator: formFileDeatils?.series800Indicator,
          sapIndicator: formFileDeatils?.sapIndicator,
          multiBranchIndicator: formFileDeatils?.multiBranchIndicator,
        }),
      },
      ...(submitterDetails?.destinationSystem === '8' && {
        demographics: {
          ...form.getValues('demographics'),
          earliestStartDate: earlyExpDate,
        },
        populationExclusions: {
          Type: typePopulation,
          TypeCodeField: typeCodeField,
          CancelReasonField: CancelReasonFied,
          EligibilityStatusField: populationEligibilityStatusField,
          PlanVariationCodeField:
            convertStrintoArray(
              data?.populationExclusions?.PlanVariationCodeField
            ) || [],
          ReportingCodeField:
            convertStrintoArray(
              data?.populationExclusions?.ReportingCodeField
            ) || [],
          MemberUtilityTextField:
            convertStrintoArray(
              data?.populationExclusions?.MemberUtilityTextField
            ) || [],
          CoverageUtilityField:
            convertStrintoArray(
              data?.populationExclusions?.CoverageUtilityField
            ) || [],
          EligibilityUtilityTextField:
            convertStrintoArray(
              data?.populationExclusions?.EligibilityUtilityTextField
            ) || [],
        },
      }),
      thresholdProcessing: {
        submitterGroupPopulation:
          +data.thresholdProcessing?.submitterGroupPopulation || 0,
        autoCancelIndicator:
          +data?.thresholdProcessing?.autoCancelIndicator || '2',
        percentages: thresholdPrecentage || [],
      },
      rulesMaintenance: ids,
    };

    const isValidated = Object.values(errors).filter(
      (error) => error !== null && error !== undefined
    ) as string[];
    setFieldErrorMessages(isValidated);
    if (isValidated?.length === 0) {
      try {
        setLoadStatus(true);
        const response = await updateProfile(finalPayload);
        if (response?.isSuccess) {
          setData(response?.data);
          dispatch(setSubmitterdata(response?.data?.customerDetails));

          const versionList = getVersionListBySubmitterGroupName(
            response?.data?.history,
            response.data?.customerDetails?.submitterGroupName
          );
          setVlist(versionList);
          setVNumber(response?.data?.customerDetails?.versionNumber);
          toggleEditMode();
          setLoadStatus(false);
          toast.show({
            title: `Profile Save `,
            message: 'Profile has been Submitted Successfully',
            type: 'success',
          });
          if (version && asgnId && srchDestSysCd) {
            router.navigate(
              `/profile/${encryptProfileParams(
                response?.data?.customerDetails?.versionNumber,
                response?.data?.customerDetails?.submitterAssignId,
                response?.data?.customerDetails?.destinationSystem
              )}`
            );
          }
        } else {
          setFieldErrorMessages(
            response?.error?.response?.data?.validationErrors
          );
          setLoadStatus(false);
          window?.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } catch (e) {
        console.log(e);
      }
    } else {
      window?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleReset = () => {
    form.reset(data);
    dispatch(SetVerEndDate(data?.customerDetails?.versionEndDate));
    dispatch(setVerStartDate(data?.customerDetails?.versionEffectiveDate));
    dispatch(setNextFileIndicator(data?.fileDetails?.nextFileExpectedDate));
    dispatch(setEarlyExpdDate(data?.demographics?.earliestStartDate));
    setCancelClick(!cancelClick);
  };

  return (
    <CustomerProfileProvider
      searchObj={{
        data,
        errMsg,
        vlist,
        search,
        vNumber,
        demList,
        setSearch,
        demIndex,
        editMode,
        setVlist,
        setDemList,
        setVNumber,
        loadStatus,
        setDemIndex,
        latestVersion,
        toggleEditMode,
        submitterList,
        submitterValue,
        onVersionChange,
        submitterDetails,
        fieldErrorMessages,
        setFieldErrorMessages,
        setSubmitterList,
        setSubmitterValue,
        fetchDataBysearch,
        setSubmitterDetails,
        updateSubmitterDeatils,
        fetchProfileBySumbitterChange,
        handleSubmit,
        cancelClick,
        handleReset,
        handleEditButton,
      }}
      profileLists={profileLists}
      form={form}
      onReset={handleReset}
    >
      <CustomerProfileCard isSearch={isSearch} />
    </CustomerProfileProvider>
  );
};

export default CustomerProfile;
